// Content table for the home Learn tile. Decoupled from LearnTile.vue so copy/links
// can be edited without touching component logic.
//
// Pinned entries appear first regardless of triggers. Triggered entries surface when
// their `triggers(state)` predicate is true and slot in next, ordered by `priority`
// (ascending). Evergreen entries (no triggers) fill remaining slots and are sampled
// per-user-per-day via daySeed in LearnTile.vue.

import type { LearnTileUserState } from "@/components/Home/LearnTile.vue";

export type LearnCard = {
	id: string;
	title: string;
	kind: string;
	href: string;
	pinned?: boolean;
	priority?: number;
	triggers?: (s: LearnTileUserState) => boolean;
};

const DOCS = "https://docs.signaloid.io";

export const LEARN_CARDS: LearnCard[] = [
	// Example pinned entry:
	// { id: "tech-explainer-0000", title: "Not Just a Faster Horse", kind: "Explainer", pinned: true,
	// 	href: "https://signaloid.com/technology-explainers/technology-explainer-0000" },

	{
		id: "api-quickstart",
		title: "Compute Engine API quickstart",
		kind: "API",
		priority: 10,
		href: `${DOCS}/docs/api/quickstart/`,
		triggers: (s) => s.apiKeyCount <= 1,
	},
	{
		id: "first-task",
		title: "Run your first task",
		kind: "Tutorial",
		priority: 11,
		href: `${DOCS}/docs/platform/getting-started/code/`,
		triggers: (s) => s.hasApiKey && s.tasksRun === 0,
	},
	{
		id: "connect-repo",
		title: "Connect a GitHub repository",
		kind: "Guide",
		priority: 12,
		href: `${DOCS}/docs/platform/getting-started/repositories/`,
		triggers: (s) => s.repos === 0,
	},
	{
		id: "connect-data",
		title: "Connect a data source",
		kind: "Guide",
		priority: 13,
		href: `${DOCS}/docs/platform/getting-started/data-sources/`,
		triggers: (s) => s.dataSources === 0 && s.tasksRun > 0,
	},
	{
		id: "custom-cores",
		title: "Custom cores quickstart",
		kind: "Guide",
		priority: 14,
		href: `${DOCS}/docs/platform/getting-started/custom-cores/`,
		triggers: (s) => s.customCores === 0 && s.tasksRun >= 3,
	},

	{
		id: "task-history",
		title: "Reading task results",
		kind: "Concept",
		priority: 20,
		href: `${DOCS}/docs/platform/getting-started/task-history/`,
		triggers: (s) => s.tasksRun >= 5,
	},
	{
		id: "uxhw-api",
		title: "UxHw API reference",
		kind: "API",
		priority: 22,
		href: `${DOCS}/docs/uxhw-api/`,
		triggers: (s) => s.tasksRun >= 5,
	},
	{
		id: "dist-from-samples",
		title: "Building distributions from samples",
		kind: "Tutorial",
		priority: 24,
		href: `${DOCS}/docs/uxhw-api/inserting-uncertainty/dist-from-samples/`,
		triggers: (s) => s.tasksRun >= 10,
	},
	{
		id: "bayes",
		title: "Bayesian inference with UxHw",
		kind: "Tutorial",
		priority: 26,
		href: `${DOCS}/docs/uxhw-api/bayesian-inference/bayes-laplace/`,
		triggers: (s) => s.tasksRun >= 10,
	},
	{
		id: "autocorr",
		title: "Manipulating correlations in UxHw",
		kind: "Tutorial",
		priority: 28,
		href: `${DOCS}/docs/uxhw-api/manipulating-correlations/get-independent-copy/`,
		triggers: (s) => s.customCores >= 1,
	},

	{ id: "uxhw-overview", title: "UxHw API overview", kind: "API", href: `${DOCS}/docs/uxhw-api/` },
	{
		id: "editor",
		title: "Using the Code Playground",
		kind: "Tip",
		href: `${DOCS}/docs/platform/user-interface/editor/`,
	},
	{
		id: "billing",
		title: "Billing and usage quotas",
		kind: "Reference",
		href: `${DOCS}/docs/platform/user-interface/billing/`,
	},
	{
		id: "reference-runs",
		title: "Reference executions",
		kind: "Reference",
		href: `${DOCS}/docs/platform/getting-started/reference-executions/`,
	},
	{
		id: "cli-quickstart",
		title: "Signaloid CLI quickstart",
		kind: "API",
		href: `${DOCS}/docs/api/signaloid-cli/intro/`,
	},
	{ id: "changelog", title: "Changelog", kind: "Changelog", href: "https://changelog.signaloid.io/" },
];
